import type {
  CanonicalFile,
  CanonicalFileWrite,
  FileSearchHit,
} from "@/lib/data/schemas/files";
import type { FirmFile, MatterFile } from "@/lib/types";

export const canDownloadFile = (file: {
  isFolder?: boolean;
  presignedUrlKey?: string | null;
  revision?: number;
  capabilities?: { canDownload: boolean };
}) =>
  !file.isFolder &&
  (file.capabilities?.canDownload ??
    Boolean(file.presignedUrlKey || file.revision));

export const canOfferFileDuplication = (file: {
  isFolder?: boolean;
  systemFolder?: string | null;
  skillId?: string | null;
  presignedUrlKey?: string | null;
  revision?: number;
  capabilities?: { canDownload: boolean };
}) =>
  !file.systemFolder &&
  !file.skillId &&
  (file.isFolder || canDownloadFile(file));

// Keep domain-only metadata (tags, template fields and source keys) on its owning
// contracts while canonical responses own identity, placement and capabilities.
export const adaptFile = <T extends FirmFile | MatterFile>(
  file: CanonicalFile | CanonicalFileWrite,
  original?: Partial<T>,
) =>
  ({
    ...original,
    ...file,
    idLocation: file.parentId,
    matterId:
      file.matterId ??
      file.massTortId ??
      (original as Partial<MatterFile> | undefined)?.matterId,
  }) as unknown as T;

export const mergeFileTree = <T extends FirmFile | MatterFile>(
  files: CanonicalFile[],
  metadata: T[],
) => {
  const byId = new Map(metadata.map((file) => [file.id, file]));
  return files.map((file) => adaptFile<T>(file, byId.get(file.id)));
};

// Search owns current placement and capabilities; repository metadata supplies
// document-generation fields and legacy storage keys used by existing actions.
export const mergeFileSearchResults = <T extends FirmFile | MatterFile>(
  files: T[],
  hits: FileSearchHit[],
): T[] => {
  const byId = new Map(files.map((file) => [file.id, file]));
  for (const hit of hits) {
    byId.set(hit.file.id, adaptFile<T>(hit.file, byId.get(hit.file.id)));
  }
  return Array.from(byId.values());
};
